import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { stringResources } from 'src/assets/config/stringResources';
import { RootState } from 'src/redux/store';
import { WashupClient } from 'src/utility/apis/crewPayApiClient';
import axios from 'src/utility/apis/crewPayAxios';
import { handleError } from 'src/utility/utils';
import { addFieldsInfo, deletePostedLedgerAction, Ledger, updateProcessing } from '@store/calculations/ledgerSlice';

export interface State {
    isLoading: boolean;
    error: boolean;
    washupList: Ledger[];
}

const initialState: State = {
    isLoading: false,
    error: false,
    washupList: [],
};

const washupSlice = createSlice({
    name: 'washup',
    initialState,
    reducers: {
        startLoading: (state) => {
            state.isLoading = true;
        },
        hasError: (state, action) => {
            state.error = action.payload;
            state.isLoading = false;
        },
        getWashupSuccess: (state, action: PayloadAction<Ledger[]>) => {
            state.washupList = action.payload;
            state.isLoading = false;
        },
        postWashupSuccess: (state) => {
            toast.success(stringResources.REDUX_SLICE.POST_SUCCESS);
            state.isLoading = false;
        },
    },
});

// Reducer
export default washupSlice.reducer

// Actions
export const {
    startLoading,
    hasError,
} = washupSlice.actions;

// for useSelector
export const selectWashup = (state: RootState) => state.washup as State;

// ----------------------------------------------------------------------
export function getWashup(fishingTripNumber: string) {
    return async (dispatch, getState) => {
        dispatch(washupSlice.actions.startLoading());
        try {
            const client = new WashupClient(undefined, axios);
            const response = await client.getEstimatedWashup(fishingTripNumber);
            const payload = await addFieldsInfo(dispatch, getState, response);

            dispatch(washupSlice.actions.getWashupSuccess(payload));
        } catch (error) {
            dispatch(washupSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function postWashup(fishingTripNumber: string) {
    return async (dispatch, getState) => {
        dispatch(washupSlice.actions.startLoading());
        try {
            const client = new WashupClient(undefined, axios);
            await client.postWashup(fishingTripNumber);
            dispatch(washupSlice.actions.postWashupSuccess());
        } catch (error) {
            dispatch(washupSlice.actions.hasError(true));
            handleError(error);
        }
    };
}

export function deletePostedWashupLedger(exportId: string) {
    return async (dispatch, getState) => {
        dispatch(updateProcessing(true));
        const toastId = toast.loading(stringResources.REDUX_SLICE.DELETE_LOADING);
        try {
            const client = new WashupClient(undefined, axios);
            await client.deleteLedgerLines(exportId);
            dispatch(deletePostedLedgerAction(exportId));
            toast.update(toastId, {
                render: stringResources.REDUX_SLICE.DELETE_SUCCESS,
                type: 'success',
                isLoading: false,
                autoClose: 3000,
            });
        } catch (error) {
            dispatch(washupSlice.actions.hasError(true));
            handleError(error, toastId);
        }
    };
}

